import React, { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { Gauge } from "lucide-react";
import { IMAGES } from "../mock";

// Phases of cinematic text tied to scroll progress
const PHASES = [
  { key: "choice", title: "LE CHOIX", sub: "Citadine, familiale, utilitaire ou premium : tout est possible." },
  { key: "trust", title: "LA CONFIANCE", sub: "Chaque véhicule inspecté, négocié et sécurisé pour vous." },
  { key: "freedom", title: "LA LIBERTÉ", sub: "Votre voiture idéale, livrée clé en main, à votre budget." },
];

const PhaseText = ({ scrollYProgress, index }) => {
  // Each phase occupies a third of the timeline with fade in/out
  const start = index / 3;
  const mid = start + 1 / 6;
  const end = (index + 1) / 3;
  const opacity = useTransform(
    scrollYProgress,
    [start, start + 0.05, end - 0.05, end],
    [0, 1, 1, 0]
  );
  const y = useTransform(scrollYProgress, [start, end], [40, -40]);
  const phase = PHASES[index];

  return (
    <motion.div
      style={{ opacity, y }}
      className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pointer-events-none"
    >
      <p className="eyebrow justify-center mb-4">Chapitre 0{index + 1}</p>
      <h2 className="section-title text-5xl md:text-8xl text-white drop-shadow-2xl">
        {phase.title.split(" ").map((w, i) => (
          <span key={i} className={i === 1 ? "racing-text" : ""}>
            {w}{" "}
          </span>
        ))}
      </h2>
      <p className="mt-5 text-white/75 text-lg md:text-xl max-w-xl">{phase.sub}</p>
    </motion.div>
  );
};

const SpeedHud = ({ scrollYProgress }) => {
  const [speed, setSpeed] = useState(0);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setSpeed(Math.round(v * 317));
  });
  return (
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 glass rounded-full px-6 py-3">
      <Gauge size={20} className="text-[#ff6a00]" />
      <span className="font-display font-extrabold text-2xl text-white tabular-nums w-[74px] text-right">
        {speed}
      </span>
      <span className="text-white/50 text-sm font-display tracking-widest">KM/H</span>
    </div>
  );
};

const ScrollVideo = () => {
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: wrapperRef,
    offset: ["start start", "end end"],
  });

  // Drive video.currentTime from scroll progress
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    const video = videoRef.current;
    if (video && video.duration && !Number.isNaN(video.duration)) {
      const target = Math.min(Math.max(v, 0), 1) * video.duration;
      // Only update if meaningfully different to reduce jitter
      if (Math.abs(video.currentTime - target) > 0.02) {
        video.currentTime = target;
      }
    }
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onLoaded = () => {
      video.pause();
      video.currentTime = 0.01;
    };
    video.addEventListener("loadedmetadata", onLoaded);
    // Attempt to buffer more of the video
    video.load();
    return () => video.removeEventListener("loadedmetadata", onLoaded);
  }, []);

  const overlayOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0.55]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1]);

  return (
    <section ref={wrapperRef} className="relative bg-black" style={{ height: "360vh" }}>
      <div className="sticky top-0 h-screen w-full overflow-hidden">
        {/* Fallback cinematic image (shows if video cannot decode) */}
        <motion.div style={{ scale }} className="absolute inset-0">
          <img
            src={IMAGES.redBmwRoad}
            alt="Véhicule sportif en mouvement"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <motion.video
          ref={videoRef}
          style={{ scale }}
          className="absolute inset-0 w-full h-full object-cover"
          src="/videos/cinematic-main.mp4"
          muted
          playsInline
          preload="auto"
        />
        {/* Dark cinematic overlays */}
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#08080a] via-transparent to-[#08080a]/70" />
        {/* Vignette */}
        <div
          className="absolute inset-0"
          style={{ boxShadow: "inset 0 0 220px 60px rgba(0,0,0,0.75)" }}
        />

        {PHASES.map((_, i) => (
          <PhaseText key={i} scrollYProgress={scrollYProgress} index={i} />
        ))}

        <SpeedHud scrollYProgress={scrollYProgress} />
      </div>
    </section>
  );
};

export default ScrollVideo;
